import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { TOW_STATUSES, STATUS_LABELS, STATUS_COLORS } from "@/lib/constants";
import Shell from "@/components/Shell";
import StatusStepper from "@/components/StatusStepper";
import ChatBox from "@/components/ChatBox";
import { StarDisplay } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PayButtons } from "@/components/PayButtons";
import { ArrowRight, Truck, MapPin, Clock, Phone, Building2 } from "lucide-react";
import { toast } from "sonner";
import { ChatAttachment } from "@/components/ChatAttachment";
import { CancelTowDialog } from "@/components/CancelTowDialog";

export default function TowDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tow, setTow] = useState(null);
  const [offers, setOffers] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/tow-requests/${id}`);
    setTow(data);
    const o = await api.get(`/tow-requests/${id}/offers`);
    setOffers(o.data);
  };
  useEffect(() => { load(); const t = setInterval(load, 7000); return () => clearInterval(t); }, [id]);

  if (!tow) return <Shell title="جارٍ التحميل..."><div /></Shell>;

  const accept = async (oid) => {
    try { await api.post(`/tow-offers/${oid}/accept`); toast.success("تم قبول عرض السحب"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const acc = tow.accepted_offer;

  return (
    <Shell title="تفاصيل طلب السطحة" subtitle={`${tow.vehicle_type} • ${tow.tow_type}`} icon={Truck} accent="accent"
      actions={<Button variant="outline" className="rounded-full gap-2" onClick={() => navigate("/app")} data-testid="back-btn"><ArrowRight className="w-4 h-4" /> رجوع</Button>}>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Badge variant="outline" className={STATUS_COLORS[tow.status]}>{STATUS_LABELS[tow.status]}</Badge>
              {tow.cancelled && <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30" data-testid="tow-cancelled-badge">ملغى</Badge>}
            </div>
            {tow.cancelled && tow.cancel_reason && <div className="mb-3 text-xs text-muted-foreground" data-testid="tow-cancel-reason">سبب الإلغاء: {tow.cancel_reason}</div>}
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-600" /> من: {tow.from_location}</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-accent" /> إلى: {tow.to_location}</div>
              {tow.workshop_code ? <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" /> ورشة رقم {tow.workshop_code}</div> : null}
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" /> وقت السحب: {tow.pickup_time}</div>
            </div>
            {tow.notes && <div className="mt-3 p-3 rounded-xl bg-muted/50 text-sm">{tow.notes}</div>}
            {tow.attachments?.length > 0 && (
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="tow-detail-photos">
                {tow.attachments.map((a) => <div key={a.file_id} className="rounded-lg overflow-hidden border border-border"><ChatAttachment att={a} compact /></div>)}
              </div>
            )}
            {user?.role === "customer" && !tow.accepted_offer_id && !tow.cancelled && (
              <div className="mt-4"><CancelTowDialog towId={id} onDone={load} /></div>
            )}
          </Card>
          {acc && (
            <Card className="p-6">
              <h3 className="font-bold mb-3">حالة السطحة</h3>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-muted-foreground" /> السائق: {acc.driver_name}</div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /> الجوال: {acc.driver_phone}</div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" /> وقت الوصول: {acc.eta}</div>
              </div>
              <StatusStepper statuses={TOW_STATUSES} current={tow.status} accent="accent" />
              {!tow.paid ? (
                <div className="mt-4">
                  <PayButtons kind="tow" refId={id} testId="pay-tow-btn" label={`ادفع قيمة السحب (${acc.price?.toFixed(0)} ر.س)`} />
                </div>
              ) : (
                <Badge className="bg-emerald-500 text-white mt-4" data-testid="tow-paid-badge">تم دفع قيمة السحب</Badge>
              )}
            </Card>
          )}

          {acc && (
            <Card className="p-6">
              <h3 className="font-bold mb-3">محادثة مع السائق</h3>
              <ChatBox threadType="tow" refId={id} meId={user?.id} />
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg">عروض السطحات ({offers.length})</h3>
          {acc ? (
            <Card className="p-5 border-emerald-500 border-2">
              <div className="flex items-center justify-between">
                <div className="font-bold">{acc.provider_name}</div>
                <Badge className="bg-emerald-500 text-white">مقبول</Badge>
              </div>
              <div className="text-lg font-extrabold text-accent mt-1">{acc.price?.toFixed(0)} ر.س</div>
            </Card>
          ) : offers.length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">بانتظار عروض مزودي السطحات...</Card>
          ) : offers.map((o) => (
            <Card key={o.id} className="p-4" data-testid={`tow-offer-${o.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold">{o.provider_name}</div>
                <div className="text-lg font-extrabold text-accent">{o.price.toFixed(0)} <span className="text-xs text-muted-foreground">ر.س</span></div>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {o.eta}</span>
                <span>{o.tow_type}</span>
                <StarDisplay value={o.provider_rating || 0} size="w-3.5 h-3.5" />
              </div>
              <Button onClick={() => accept(o.id)} className="w-full rounded-xl bg-accent hover:bg-accent/90" data-testid={`accept-tow-${o.id}`}>قبول العرض</Button>
            </Card>
          ))}
        </div>
      </div>
    </Shell>
  );
}
